import {
  getFunctionPeelIndices, getIsExpressionGroupWithoutOperators,
} from '~/dashboard/views/workflows/store/engine/utils';
import { getNumericPathIndices } from '~/dashboard/views/workflows/service/actions';
import { RuleEditorDropdownMode } from '../NodeEditorDrawer/types';
import { getIsFunctionContainer } from './expressionNode';
import type { ExpressionNode } from './expressionNode';

export enum ExpressionDeletionType {
  /** delete the item, along with any data route it used */
  ITEM = 'item',
  /** delete the data route, which removes its item from the expression too */
  DATA_ROUTE = 'dataRoute',
  GROUP = 'group',
  UNGROUP = 'ungroup',
  /** remove the group's last item. Pressing backspace again takes the next one. */
  LAST_ITEM = 'lastItem',
  NONE = 'none',
}

export type ExpressionDeletion = {
  type: ExpressionDeletionType;
  /** what actually gets deleted, which is not always the path that was asked about */
  path: string;
  /** where to leave the caret, for positions where it cannot just step back one */
  caretPath?: string;
  /** leave the caret just inside the group, when no item is left to sit after */
  caretInsideGroupPath?: string;
};

const none: ExpressionDeletion = { type: ExpressionDeletionType.NONE, path: '' };

/**
 * Works out where the caret goes once a node is deleted.
 *
 * A group's own min/max is skipped, because it is drawn as a comma and has no caret position of its
 * own. Arithmetic operators do have one, so deleting the group in a * Max ( ) leaves the caret on
 * the *, not on a.
 */
function caretBesideNode(node: ExpressionNode) {
  const ownIndex = Number(getNumericPathIndices(node.path).at(-1));
  const { items, operator, path } = node.container;
  const previousIndex = items
    .slice(0, ownIndex)
    .findLastIndex(item => item !== operator);

  return previousIndex >= 0
    ? { caretPath: `${path}[${previousIndex}]` }
    : { caretInsideGroupPath: path };
}

export function resolveExpressionDeletion({
  node,
  mode,
  isCaretPastFunction,
  hasDataRoute,
}: {
  node?: ExpressionNode;
  mode: RuleEditorDropdownMode;
  /** the caret is just after a closed min/max group, or inside an empty one */
  isCaretPastFunction?: boolean;
  hasDataRoute?: boolean;
}): ExpressionDeletion {
  if (!node) return none;

  if (mode === RuleEditorDropdownMode.CARET_EXPRESSION) {
    if (!isCaretPastFunction) return none;

    // a min/max group with no arguments left is deleted, not turned into an ordinary group
    if (node.group?.awaitingOperands) {
      return {
        type: ExpressionDeletionType.GROUP,
        path: node.path,
        // the group's position disappears with it, so the caret has to move
        ...caretBesideNode(node),
      };
    }

    const peel = getFunctionPeelIndices(node.group ? node.item as never : []);

    if (!peel) return none;

    return {
      type: ExpressionDeletionType.ITEM,
      path: `${node.path}[${peel.peelIndex}]`,
      ...(peel.previousOperandIndex >= 0
        ? { caretPath: `${node.path}[${peel.previousOperandIndex}]` }
        : { caretInsideGroupPath: node.path }),
    };
  }

  if (hasDataRoute) return { type: ExpressionDeletionType.DATA_ROUTE, path: node.path };

  if (mode === RuleEditorDropdownMode.EXPRESSION) {
    return node.item === undefined
      ? none
      : { type: ExpressionDeletionType.ITEM, path: node.path };
  }

  const isGroupParenthesis = mode === RuleEditorDropdownMode.GROUP_START
    || mode === RuleEditorDropdownMode.GROUP_END;

  if (isGroupParenthesis && node.group) {
    if (getIsFunctionContainer(node.group)) {
      const peel = getFunctionPeelIndices(node.item as never);

      return peel
        ? { type: ExpressionDeletionType.LAST_ITEM, path: `${node.path}[${peel.peelIndex}]` }
        : { type: ExpressionDeletionType.GROUP, path: node.path };
    }

    // removing the parentheses would move its operators into the min/max argument list and change
    // the result, so take items off one at a time instead
    const isArgumentOfFunction = getIsFunctionContainer(node.container);
    const wouldSpliceOperatorsIntoFunction = isArgumentOfFunction
      && !getIsExpressionGroupWithoutOperators(node.item as never);

    return wouldSpliceOperatorsIntoFunction
      ? {
        type: ExpressionDeletionType.LAST_ITEM,
        path: `${node.path}[${(node.item as unknown[]).length - 1}]`,
      }
      : { type: ExpressionDeletionType.UNGROUP, path: node.path };
  }

  return none;
}
